import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from "@angular/cdk/drag-drop";

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, TemplateRef, ChangeDetectionStrategy } from "@angular/core";
import { FormArray, FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, ValidatorFn } from "@angular/forms";
import { cloneDeep, isEmpty, some } from "lodash";
import { IPanelGroup, PanelGroupType } from "rl-common/components/mod-details-layout/mod-layout.models";
import { ComponentChanges } from "rl-common/models/i-component-change";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { Subscription } from "rxjs";
import { filter, tap } from "rxjs/operators";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";


export interface ISubTabForm {
	sections: FormArray<FormControl<IPanelGroup>>;
}

@Component({
	selector: "rl-edit-panel-sub-tabs-modal",
	templateUrl: "./edit-panel-sub-tabs-modal.component.html",
	styleUrls: ["./edit-panel-sub-tabs-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [CdkDropList, CdkDrag, CdkDragHandle, TextInputComponent, ReactiveFormsModule, FormsModule]
})
export class EditPanelSubTabsModalComponent implements OnInit, OnDestroy, OnChanges {

	@Input()
	panel: IPanelGroup;

	form: FormGroup<ISubTabForm>;
	newGroupName: string = ``;

	@Output()
	onComplete = new EventEmitter<IPanelGroup>();

	_subs: Subscription[] = [];

	get sections() {
		return this.form.controls.sections.value;
	}

	get isValid() {
		return this.form.valid;
	}

	constructor(
		private readonly _formBuilder: FormBuilder,
		private readonly _promptService: PromptService
	) { }

	ngOnInit() {
		this.buildForm(this.panel);
	}

	ngOnChanges(changes: ComponentChanges<this>): void {
		if (changes.panel && this.panel) {
			this.buildForm(this.panel);
		}
	}

	buildForm(panel: IPanelGroup = null) {
		const childSections = panel?.childGroups ?? [];
		const formControls = childSections.map(x => new FormControl<IPanelGroup>(cloneDeep(x)));
		this.form = this._formBuilder.group({
			sections: new FormArray(formControls)
		}, { validators: subTabsValidator });
	}

	dropSection(event: CdkDragDrop<IPanelGroup[]>) {
		const sections = this.sections;
		moveItemInArray(sections, event.previousIndex, event.currentIndex);
		this.form.controls.sections.setValue(sections);
	}

	moveToTop(previousIndex: number) {
		const sections = this.sections;
		moveItemInArray(sections, previousIndex, 0);
		this.form.controls.sections.setValue(sections);
	}

	addSection() {
		const control = new FormControl<IPanelGroup>({ title: this.newGroupName, type: PanelGroupType.Configurable });
		this.form.controls.sections.push(control);
		this.newGroupName = ``;
	}

	close() {
		this.onComplete.emit(null);
	}

	done() {
		const panel = cloneDeep(this.panel);
		panel.childGroups = this.form.controls.sections.value;
		this.onComplete.emit(panel);
	}

	deleteSection(deletePrompt: TemplateRef<unknown>, index: number) {
		const section = this.sections[index];
		// don't bother to prompt if the group is empty
		if (isEmpty(section.sections)) {
			this.removeSection(index);
			return;
		}

		const sub = this._promptService.confirmMini(`Delete Section`, deletePrompt, { group: section }).pipe(
			filter(result => !!result),
			tap(() => this.removeSection(index))
		).subscribe();

		this._subs.push(sub);
	}

	private removeSection(index: number) {
		this.form.controls.sections.removeAt(index);
	}

	ngOnDestroy(): void {
		this._subs.forEach(x => x.unsubscribe());
	}
}

export const subTabsValidator: ValidatorFn = (form: FormGroup<ISubTabForm>): ValidationErrors | null => {
	const panelGroupsArray = form.controls.sections;
	if (isEmpty(panelGroupsArray.controls)) {
		return { sectionsRequired: true };
	}
	const titleCounts: { [title: string]: number } = panelGroupsArray.value.reduce((acc, next) => {
		const lowerTitle = next.title.trim().toLocaleLowerCase();
		if (lowerTitle in acc) {
			acc[lowerTitle] += 1;
		} else {
			acc[lowerTitle] = 1;
		}
		return acc;
	}, {});

	const hasDupes = some(Object.values(titleCounts), x => x > 1);
	if (hasDupes) {
		return { titleUniqueness: true };
	}

	return null;
};